// TipLock Portal API Slice - Monitoring, Alerts, Events, Suppression
// All endpoints point to /api/tiplock/ which filters data to Universal API account
import { createApi } from '@reduxjs/toolkit/query/react';
import { getApiBaseUrl } from '../../../Features/API/api';
import { baseQueryWithReauth } from '../../../Features/API/baseQueryWithReauth';

const BASE_URL = getApiBaseUrl();

export const tiplockApiSlice = createApi({
  reducerPath: 'tiplockApi',
  baseQuery: baseQueryWithReauth(BASE_URL),
  tagTypes: [
    'TiplockAlert',
    'TiplockEvent',
    'TiplockGroup',
    'TiplockVehicle',
    'TiplockDevice',
    'TiplockSuppressionProfile',
    'TiplockDeviceOverride',
    'TiplockSuppressionStats',
  ],
  endpoints: (builder) => ({
    // ========== FLEET ENDPOINTS ==========
    // Main device groups endpoint - returns hierarchical tree structure
    fetchTiplockDeviceGroups: builder.query({
      query: () => 'tiplock/groups/',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'TiplockGroup', id })),
              { type: 'TiplockGroup', id: 'LIST' },
            ]
          : [{ type: 'TiplockGroup', id: 'LIST' }],
    }),

    // Alias for backwards compatibility
    fetchTiplockGroups: builder.query({
      query: () => 'tiplock/groups/',
      providesTags: ['TiplockGroup'],
    }),

    fetchTiplockGroupDetail: builder.query({
      query: (groupId) => `tiplock/groups/${groupId}/`,
      providesTags: (result, error, id) => [{ type: 'TiplockGroup', id }],
    }),

    updateTiplockGroup: builder.mutation({
      query: ({ groupId, ...data }) => ({
        url: `tiplock/groups/${groupId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { groupId }) => [
        { type: 'TiplockGroup', id: groupId },
        'TiplockGroup',
      ],
    }),

    fetchTiplockVehicles: builder.query({
      query: () => 'tiplock/vehicles/',
      providesTags: ['TiplockVehicle'],
    }),

    fetchTiplockVehicleDetail: builder.query({
      query: (vehicleId) => `tiplock/vehicles/${vehicleId}/`,
      providesTags: (result, error, id) => [{ type: 'TiplockVehicle', id }],
    }),

    fetchTiplockDevices: builder.query({
      query: () => 'tiplock/devices/',
      providesTags: ['TiplockDevice'],
    }),

    fetchTiplockDeviceDetail: builder.query({
      query: (deviceId) => `tiplock/devices/${deviceId}/`,
      providesTags: (result, error, id) => [{ type: 'TiplockDevice', id }],
    }),

    // ========== MONITORING ENDPOINTS ==========
    fetchTiplockAlerts: builder.query({
      query: (params = {}) => ({
        url: 'tiplock/alerts/',
        params: {
          include_suppressed: true,
          ...params,
        },
      }),
      providesTags: ['TiplockAlert'],
    }),

    fetchTiplockAlertDetail: builder.query({
      query: (alertId) => `tiplock/alerts/${alertId}/`,
      providesTags: (result, error, id) => [{ type: 'TiplockAlert', id }],
    }),

    updateTiplockAlert: builder.mutation({
      query: ({ alertId, ...data }) => ({
        url: `tiplock/alerts/${alertId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { alertId }) => [
        { type: 'TiplockAlert', id: alertId },
        'TiplockAlert',
      ],
    }),

    discardTiplockAlert: builder.mutation({
      query: ({ alert_id, reason, notes }) => ({
        url: 'tiplock/alerts/discard/',
        method: 'POST',
        body: { alert_id, reason, notes },
      }),
      invalidatesTags: ['TiplockAlert'],
    }),

    // Poll alerts for real-time updates
    pollTiplockAlerts: builder.query({
      query: (params = {}) => ({
        url: 'tiplock/alerts/',
        params: {
          include_suppressed: true,
          ...params,
        },
      }),
      providesTags: ['TiplockAlert'],
    }),

    // ========== EVENT ENDPOINTS ==========
    fetchTiplockEvents: builder.query({
      query: (params = {}) => ({
        url: 'tiplock/events/',
        params,
      }),
      providesTags: ['TiplockEvent'],
    }),

    fetchTiplockEventDetail: builder.query({
      query: (eventId) => `tiplock/events/${eventId}/`,
      providesTags: (result, error, id) => [{ type: 'TiplockEvent', id }],
    }),

    createTiplockEvent: builder.mutation({
      query: (formData) => ({
        url: 'tiplock/events/create/',
        method: 'POST',
        body: formData,
        // Don't set Content-Type - let browser set it for FormData with multipart/form-data
        formData: true,
      }),
      invalidatesTags: ['TiplockEvent', 'TiplockAlert'],
    }),

    // ========== SUPPRESSION ENDPOINTS ==========
    fetchTiplockSuppressionProfiles: builder.query({
      query: () => 'tiplock/suppression/profiles/',
      providesTags: ['TiplockSuppressionProfile'],
    }),

    fetchTiplockSuppressionStats: builder.query({
      query: () => 'tiplock/suppression/stats/',
      providesTags: ['TiplockSuppressionStats'],
    }),

    fetchTiplockDeviceOverrides: builder.query({
      query: () => 'tiplock/suppression/overrides/',
      providesTags: ['TiplockDeviceOverride'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Fleet hooks
  useFetchTiplockDeviceGroupsQuery,
  useFetchTiplockGroupsQuery,
  useFetchTiplockGroupDetailQuery,
  useUpdateTiplockGroupMutation,
  useFetchTiplockVehiclesQuery,
  useFetchTiplockVehicleDetailQuery,
  useFetchTiplockDevicesQuery,
  useFetchTiplockDeviceDetailQuery,
  // Alert hooks
  useFetchTiplockAlertsQuery,
  useFetchTiplockAlertDetailQuery,
  useUpdateTiplockAlertMutation,
  useDiscardTiplockAlertMutation,
  usePollTiplockAlertsQuery,
  // Event hooks
  useFetchTiplockEventsQuery,
  useFetchTiplockEventDetailQuery,
  useCreateTiplockEventMutation,
  // Suppression hooks
  useFetchTiplockSuppressionProfilesQuery,
  useFetchTiplockSuppressionStatsQuery,
  useFetchTiplockDeviceOverridesQuery,
} = tiplockApiSlice;
